import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Col, Loading, Row } from '@commonsku/styles';

import NewProduct from '../NewProduct';
import { PAGE_SIZE } from './ProductSearchFilter';
import { getImageSrc, makeCancelable, oauth } from '../../utils';

/** Distance from bottom of scroll container (px) before loading the next page. */
const SCROLL_LOAD_THRESHOLD_PX = 100;

/** One row from `GET /catalogs?client_id=` */
export type Catalog = {
  catalog_id?: string;
  date_updated?: string;
};

type CatalogItemImage = {
  file_id?: string;
  image?: { file_id?: string; s3?: number; file_name_medium?: string };
};

/** Subset of `GET /catalogs/{id}/items` row fields used by this component. */
type CatalogItemRow = {
  item_id?: string;
  item_name?: string;
  image_id?: string;
  item_images?: CatalogItemImage[];
  division_name?: string;
};

/** Minimal fields read from Redux item entities for catalog Remove resolution. */
type CatalogResolveItemRow = {
  order_id?: string;
  item_id?: string;
  catalog_item_id?: string;
};

export type ClientCatalogProductSearchFilterProps = {
  client_id?: string | null;
  order_id?: string | null;
  order_type?: string;
  user_search?: { search_term?: string };
  handleStoringSearch?: (field: string, value: unknown) => void;
  handleClearingSearch?: () => void;
  getTotalProducts?: (n: number) => void;
  clearNewProduct?: () => void;
  onSearch?: () => void;
  onClear?: () => void;
  style?: React.CSSProperties;
  index?: number | null;
  loading?: string[];
  loaded?: string[];
  onAddProduct?: (
    order_id: string | null,
    order_type: string | undefined,
    product_source: null,
    target_type: string,
    index: number | null | undefined,
    sage_connect?: string
  ) => (catalogSourceItemId: string) => unknown;
  onDeleteItem?: (order_item_id: string) => unknown;
};

function buildCatalogTileProduct(item: CatalogItemRow): { product_id: string; product_img?: string } {
  const defaultImage = item.item_images?.map(ii => ii.image).find(Boolean);
  const product_img = defaultImage
    ? getImageSrc(defaultImage, 'medium')
    : item.image_id && item.image_id !== ''
      ? getImageSrc({ file_id: String(item.image_id), s3: 1 }, 'medium')
      : undefined;

  return {
    product_id: item.item_id,
    product_img,
  };
}

export default function ClientCatalogProductSearchFilter({
  client_id,
  order_id,
  order_type,
  user_search,
  handleStoringSearch,
  handleClearingSearch,
  getTotalProducts,
  clearNewProduct,
  onSearch: onSearchProp,
  onClear,
  style,
  index,
  loading: loadingIds = [],
  loaded = [],
  onAddProduct,
  onDeleteItem,
}: ClientCatalogProductSearchFilterProps) {
  const activeSearchesRef = useRef<Array<{ cancel: () => void }>>([]);
  /** Monotonic id so stale catalog fetch responses do not overwrite newer results. */
  const requestSeqRef = useRef(0);
  const catalogIdRef = useRef<string | null>(null);
  const itemsCursorRef = useRef<string | null>(null);
  const itemsRef = useRef<CatalogItemRow[]>([]);
  /** Catalog source line `item_id` → order line `item_id` after copy (for Remove). */
  const [orderItemIdByCatalogItemId, setOrderItemIdByCatalogItemId] = useState<Record<string, string>>({});
  const entitiesItems = useSelector(
    (state: { entities?: { items?: Record<string, CatalogResolveItemRow> } }) =>
      state.entities?.items ?? {}
  );

  const [search, setSearch] = useState({
    search_term: user_search?.search_term || '',
  });
  const [items, setItems] = useState<CatalogItemRow[]>([]);
  const [total_products, setTotalProductsState] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  /** Resolve catalog order id via `GET /catalogs?client_id=` */
  const resolveCatalog = useCallback(
    async (clientId: string | null | undefined): Promise<boolean> => {
      if (!clientId) {
        catalogIdRef.current = null;
        return false;
      }

      const { json } = await oauth('GET', 'catalogs', { client_id: clientId, page_limit: 1 });
      const row = (json.data as Catalog[] | undefined)?.[0];
      if (!row?.catalog_id) {
        catalogIdRef.current = null;
        return false;
      }
      catalogIdRef.current = row.catalog_id;
      return true;
    },
    []
  );

  /**
   * Loads pages from `GET /catalogs/{catalog_id}/items` only (cursor pagination).
   */
  const fetchCatalogItems = useCallback(
    async (
      pageNum: number,
      clear: boolean,
      searchTerm: string,
      clientId: string
    ): Promise<void> => {
      requestSeqRef.current += 1;
      const seq = requestSeqRef.current;
      const isCurrent = () => seq === requestSeqRef.current;

      const handleFetchError = (e: unknown): void => {
        if (!isCurrent()) {
          return;
        }
        setLoading(false);
        const error = e ?? new Error('unknown');
        if (typeof error === 'object') {
          (error as { catalogFetchSeq?: number }).catalogFetchSeq = seq;
        }
        throw error;
      };

      if (clear) {
        itemsCursorRef.current = null;
        itemsRef.current = [];
        setItems([]);
        setTotalProductsState(undefined);
        setPage(0);
      }
      setLoading(true);

      if (!catalogIdRef.current) {
        let ok: boolean;
        try {
          ok = await resolveCatalog(clientId);
        } catch (e) {
          handleFetchError(e);
        }

        if (!isCurrent()) {
          return;
        }

        if (!ok || !catalogIdRef.current) {
          setLoading(false);
          itemsRef.current = [];
          setItems([]);
          setTotalProductsState(0);
          setPage(0);
          getTotalProducts?.(0);
          return;
        }
      }

      try {
        const params: Record<string, string | number> = {
          page_limit: PAGE_SIZE,
        };
        if (searchTerm) {
          params.search = searchTerm;
        }
        if (!clear && pageNum > 0 && itemsCursorRef.current) {
          params.cursor = itemsCursorRef.current;
        }

        const catalogId = catalogIdRef.current;
        const { json } = await oauth('GET', `catalogs/${catalogId}/items`, params);

        if (!isCurrent()) {
          return;
        }

        const rows = (json.data as CatalogItemRow[]) || [];
        const meta = json.meta as { cursor?: string; next_page?: string } | undefined;
        const merged =
          clear || pageNum === 0 ? rows : [...itemsRef.current, ...rows];
        const hasMore = !!(meta?.cursor || meta?.next_page);
        const nextTotal = hasMore ? merged.length + PAGE_SIZE : merged.length;

        itemsRef.current = merged;
        setItems(merged);
        setTotalProductsState(nextTotal);
        getTotalProducts?.(nextTotal);

        setPage(pageNum);
        setLoading(false);
        itemsCursorRef.current = meta?.cursor ?? null;
      } catch (e) {
        handleFetchError(e);
      }
    },
    [getTotalProducts, resolveCatalog]
  );

  useEffect(() => {
    catalogIdRef.current = null;
    itemsCursorRef.current = null;
    itemsRef.current = [];
    setItems([]);
    setTotalProductsState(undefined);
    setPage(0);

    if (!client_id) {
      return;
    }

    void fetchCatalogItems(0, true, '', client_id);
  }, [client_id, fetchCatalogItems]);

  useEffect(
    () => () => {
      activeSearchesRef.current.forEach(x => x.cancel());
    },
    []
  );

  const onErrorResponse = useCallback(
    (json?: { error?: string; exception?: string }, errorSeq?: number) => {
      if (errorSeq !== undefined && errorSeq !== requestSeqRef.current) {
        return;
      }
      if (json && json.error) {
        toast.error(json.exception || json.error, {
          autoClose: 6000,
        });
      }
      setLoading(false);
    },
    []
  );

  const executeCatalogSearch = useCallback(
    async (pageNum: number, clear: boolean, searchTerm: string, clientId: string) => {
      await fetchCatalogItems(pageNum, clear, searchTerm, clientId);
    },
    [fetchCatalogItems]
  );

  const runSearch = useCallback(
    (pageNum: number, clear?: boolean) => {
      onSearchProp?.();
      clearNewProduct?.();

      const fetchPromise = executeCatalogSearch(pageNum, !!clear, search.search_term, client_id);
      const cancelable = makeCancelable(fetchPromise);
      activeSearchesRef.current.push(cancelable);
      cancelable.promise.then(
        () => {},
        err => {
          if (err && typeof err === 'object' && 'isCanceled' in err && err.isCanceled) {
            return;
          }

          const errObj = err && typeof err === 'object' ? (err as { json?: unknown; catalogFetchSeq?: number }) : null;
          const json = errObj && 'json' in errObj ? errObj.json : err;
          onErrorResponse(json as { error?: string; exception?: string }, errObj?.catalogFetchSeq);
        }
      );
    },
    [clearNewProduct, client_id, executeCatalogSearch, onErrorResponse, onSearchProp, search.search_term]
  );

  const handleClickSearch = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      runSearch(0, true);
    },
    [runSearch]
  );

  const handlePressEnter = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        runSearch(0, true);
      }
    },
    [runSearch]
  );

  const handleLoadNextPage = useCallback(() => {
    if (total_products && items.length < total_products && !loading) {
      runSearch(page + 1);
    }
  }, [loading, page, items.length, runSearch, total_products]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const t = event.target as HTMLDivElement;
      if (t.scrollHeight - t.scrollTop - t.clientHeight <= SCROLL_LOAD_THRESHOLD_PX) {
        handleLoadNextPage();
      }
    },
    [handleLoadNextPage]
  );

  const handleChangeSearchTerm = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const search_term = e.target.value;
      setSearch(s => ({ ...s, search_term }));
      if (e.target.value) {
        handleStoringSearch?.('search_term', e.target.value);
      }
    },
    [handleStoringSearch]
  );

  const handleClickReset = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onClear?.();
      catalogIdRef.current = null;
      itemsCursorRef.current = null;
      setSearch({ search_term: '' });
      itemsRef.current = [];
      setItems([]);
      setTotalProductsState(undefined);
      setPage(0);
      handleClearingSearch?.();
      if (client_id) {
        void fetchCatalogItems(0, true, '', client_id);
      }
    },
    [client_id, fetchCatalogItems, handleClearingSearch, onClear]
  );

  const resolveOrderItemIdForCatalogItemId = useCallback(
    (catalogItemId: string): string | undefined => {
      const fromMap = orderItemIdByCatalogItemId[catalogItemId];
      if (fromMap) {
        return fromMap;
      }

      if (!order_id) {
        return undefined;
      }

      const rows = Object.values(entitiesItems) as CatalogResolveItemRow[];
          const matchingOrderItem = rows.find(
        row =>
          row &&
          row.order_id === order_id &&
          row.catalog_item_id === catalogItemId
      );

      if (matchingOrderItem && matchingOrderItem.item_id != null) {
        return matchingOrderItem.item_id
      }

      return undefined;
    },
    [entitiesItems, orderItemIdByCatalogItemId, order_id]
  );

  const isCatalogItemIdOnOrder = useCallback(
    (catalogItemId: string): boolean => {
      if (!order_id) {
        return false;
      }
      const rows = Object.values(entitiesItems) as CatalogResolveItemRow[];

      return rows.some(
        row =>
          row &&
          row.order_id === order_id &&
          row.catalog_item_id === catalogItemId
      );
    },
    [entitiesItems, order_id]
  );

  const isCatalogRowLoadedUi = useCallback(
      (catalogItemId: string) => loaded.includes(catalogItemId) || isCatalogItemIdOnOrder(catalogItemId),
    [isCatalogItemIdOnOrder, loaded]
  );

  /** Drop local order-item mapping when neither session `loaded` nor persisted order lines reference this catalog row. */
  useEffect(() => {
    setOrderItemIdByCatalogItemId(prev => {
      const next = { ...prev };
      let changed = false;
      for (const catalogItemId of Object.keys(next)) {
        if (!loaded.includes(catalogItemId) && !isCatalogItemIdOnOrder(catalogItemId)) {
          delete next[catalogItemId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [loaded, isCatalogItemIdOnOrder]);

  const handleCatalogItemAction = useCallback(
    async (catalogItemId: string) => {
      if (loadingIds.includes(catalogItemId)) {
        return;
      }

      /** If the catalog item is already loaded or on the order, delete the order item. */
      if (isCatalogRowLoadedUi(catalogItemId)) {
        const orderItemId = resolveOrderItemIdForCatalogItemId(catalogItemId);
        if (orderItemId) {
          onDeleteItem?.(orderItemId);
          setOrderItemIdByCatalogItemId(prev => {
            const next = { ...prev };
            delete next[catalogItemId];
            return next;
          });
        }
        return;
      }

      /** Otherwise, add the catalog item to the order. */
      const addFn = onAddProduct?.(order_id, order_type, null, 'COPY', index, '0');
      if (!addFn) {
        return;
      }

      try {
        const result = await addFn(catalogItemId);
        if (result && typeof result === 'object' && 'item_id' in result) {
          const itemId = (result as { item_id: string }).item_id;
          if (itemId) {
            setOrderItemIdByCatalogItemId(prev => ({
              ...prev,
              [catalogItemId]: itemId,
            }));
          }
        }
      } catch {
        // Failure toast / state handled by add-item flow
      }
    },
    [
      loadingIds,
      isCatalogRowLoadedUi,
      onAddProduct,
      onDeleteItem,
      order_id,
      order_type,
      index,
      resolveOrderItemIdForCatalogItemId,
    ]
  );

  const renderProductRows = () => {
    if (total_products === undefined) {
      return null;
    }
    if (!items.length) {
      return null;
    }
    return (
      <>
        {items.map((item, idx) => {
          const itemId = String(item.item_id ?? '');
          const title = typeof item.item_name === 'string' ? item.item_name : '';
          const subtitle = typeof item.division_name === 'string' ? item.division_name : '';
          return (
            <NewProduct
              key={itemId || idx}
              product={buildCatalogTileProduct(item)}
              sku={''}
              description={''}
              isProductsPage={false}
              title={title}
              subtitle={subtitle}
              loading={loadingIds.includes(itemId)}
              loaded={isCatalogRowLoadedUi(itemId)}
              onClickProduct={() => {}}
              onClickProductAction={() => {
                void handleCatalogItemAction(itemId);
              }}
            />
          );
        })}
      </>
    );
  };

  return (
    <>
      <Row style={{ paddingTop: '1rem' }}>
        <div className="medium-8 columns">
          <input
            type="text"
            placeholder="Search term"
            onChange={handleChangeSearchTerm}
            value={search.search_term || ''}
            onKeyDown={e => handlePressEnter(e)}
          />
        </div>
        <div className="medium-4 columns">
          <div className="button-group expanded">
            <a className="button" onClick={e => handleClickSearch(e)}>
              Search
            </a>
            <a className="button" onClick={handleClickReset}>
              Reset
            </a>
          </div>
        </div>
      </Row>
      <Row style={{ overflow: 'auto', ...style }} onScroll={handleScroll}>
        {renderProductRows()}
        {loading ? (
          <Col xs={12} className="row full-width text-center">
            <Loading mt={16} mb={16} />
          </Col>
        ) : null}
      </Row>
    </>
  );
}
